import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

const REGIONS = [
  { key: "Southeast Asia", title: "Southeast Asia", note: "The Mekong, the islands, the lantern lit lanes." },
  { key: "East Asia", title: "East Asia", note: "From Kyoto's quiet temples to the high Himalayan plateau." },
  { key: "Beyond", title: "Beyond Asia", note: "Sri Lanka, the Maldives and the Arabian Peninsula." },
];

export default function Destinations() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/destinations").then((r) => setItems(r.data || [])); }, []);

  const grouped = REGIONS.map(r => ({ ...r, items: items.filter(d => d.region === r.key) }));

  return (
    <PublicLayout>
      <Seo
        title="Destinations — Private Journeys Across Asia | Abbot Asia"
        description="Explore Abbot Asia's destinations — Vietnam, Japan, Cambodia, Thailand, Sri Lanka and beyond. Privately guided, expertly planned journeys across Asia."
        path="/destinations"
      />
      <section className="pt-40 pb-20 bg-ivory">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <p className="eyebrow">Destinations</p>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl text-jade font-light leading-[1.02] max-w-4xl">
            Private journeys across Asia, <span className="italic">carefully designed.</span>
          </h1>
          <p className="mt-8 text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.85] max-w-2xl">
            Each destination here is one our team has walked, repeatedly, for many years. We have not added countries quickly. We have added them only when we have been certain we could deliver them privately, properly, and to the standard our travellers expect.
          </p>
        </div>
      </section>

      <section data-testid="destinations-list" className="pb-32 bg-ivory">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 space-y-24">
          {grouped.map((g) => (
            g.items.length > 0 && (
              <div key={g.key} data-testid={`region-${g.key.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-end justify-between gap-6 mb-12 border-b border-mist/60 pb-6">
                  <div>
                    <p className="eyebrow">Region</p>
                    <h2 className="mt-3 font-serif text-3xl md:text-4xl text-jade font-light">{g.title}</h2>
                  </div>
                  <p className="hidden md:block text-ink/55 text-[14px] italic max-w-sm text-right">{g.note}</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16">
                  {g.items.map((d) => (
                    <Link
                      key={d.slug}
                      to={`/destinations/${d.slug}`}
                      data-testid={`destination-card-${d.slug}`}
                      className="group block"
                    >
                      <div className="aspect-[3/4] overflow-hidden bg-sand">
                        <img
                          src={mediaUrl(d.featured_image || d.hero_image)}
                          alt={d.name}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                        />
                      </div>
                      <div className="mt-5">
                        <p className="eyebrow text-copper text-[10px]">{d.card_subtitle || d.tagline}</p>
                        <h3 className="mt-2 font-serif text-xl md:text-2xl text-jade leading-tight">{d.card_title || d.name}</h3>
                        <p className="mt-2 text-ink/65 text-[13px] font-light leading-relaxed">{(d.summary || "").split(".").slice(0,1).join(".")}.</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
